import {
  BillingType,
  CurrentSubscriptionDTO,
  DayOfWeek,
  Membership,
  MembershipBean,
  MembershipType,
  SubscriptionMembershipPreviousDTO,
  UserMemberSubscriptionBean,
  UserMemberSubscriptionBeanWithMembership,
} from "@gymflow/types";
import { capitalize } from "lodash";
import { DateTime, DateTimeUnit } from "luxon";

export const mapWeekdayToNumber: Record<DayOfWeek, number> = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
};

const getBillingDay = (
  item:
    | UserMemberSubscriptionBean
    | CurrentSubscriptionDTO
    | SubscriptionMembershipPreviousDTO,
) => {
  return item.billingType === "MONTHLY"
    ? `${item.monthlyBillingDay}`
    : capitalize(item.weeklyBillingDay);
};
const isCurrentSubscription = (sub?: UserMemberSubscriptionBean) =>
  sub &&
  (sub.active ||
    ["ACTIVE", "PAUSED", "PENDING", "OVERDUE"].includes(sub.status)) &&
  !!sub.membershipBean;
/**
 * Examples:
 * - billingType == WEEKLY && calculateProrate == NO -> $startDate + $billingPeriod weeks (ex: Feb 1st + 2 weeks == Feb 15th)
 * - billingType == WEEKLY && calculateProrate !== NO -> next $weeklyBillingDay after $startDate (next monday after Apr 29th == May 5th)
 * - billingType == MONTHLY && calculateProrate == NO -> $startDate + $billingPeriod months (ex: Feb 1st + 1 month == March 1st)
 * - billingType == MONTHLY && calculateProrate !== NO -> next $monthlyBillingDay after $startDate (next 15th after Apr 29th == May 15th)
 */
const calculateRecurringDates = (
  membership: MembershipBean,
  startDate: DateTime,
) => {
  if (!(membership.type === "RECURRING" && membership.billingType))
    throw new Error("Invalid Membership: Should be recurring");
  const { billingType, calculateProrata } = membership;
  if (billingType === "WEEKLY" && calculateProrata === "NO") {
    return startDate.plus({
      weeks: membership.billingPeriod,
    });
  }
  if (billingType === "WEEKLY" && calculateProrata !== "NO") {
    return startDate.plus({
      days:
        (7 -
          startDate.weekday +
          mapWeekdayToNumber[membership.weeklyBillingDay!]) %
        7,
    });
  }
  if (billingType === "MONTHLY" && calculateProrata === "NO") {
    return startDate.day < membership.monthlyBillingDay!
      ? startDate.set({ day: membership.monthlyBillingDay! })
      : startDate
          .plus({ months: 1 })
          .set({ day: membership.monthlyBillingDay! });
  }
  if (billingType === "MONTHLY" && calculateProrata !== "NO") {
    return startDate.plus({
      months: membership.billingPeriod,
    });
  }
  throw Error("Non supported billingType");
};

const getBillingRecurrence = (membershipBean: {
  type: MembershipType;
  billingPeriod: number;
  billingType?: BillingType;
}) => {
  return membershipBean.billingType && membershipBean.type === "RECURRING"
    ? `Per ${
        membershipBean.billingPeriod > 1
          ? `${membershipBean.billingPeriod} `
          : ``
      }${
        (
          {
            MONTHLY: "Month",
            WEEKLY: "Week",
          } as const
        )[membershipBean.billingType]
      }${membershipBean.billingPeriod !== 1 ? "s" : ""}`
    : "Once off";
};

const getSubscriptionBillingRecurrence = (
  subscription: UserMemberSubscriptionBeanWithMembership,
) => {
  return subscription.billingType &&
    subscription.billingPeriod &&
    subscription.membershipBean.type === "RECURRING"
    ? `Per ${
        subscription.billingPeriod > 1 ? `${subscription.billingPeriod} ` : ``
      }${
        (
          {
            MONTHLY: "Month",
            WEEKLY: "Week",
          } as const
        )[subscription.billingType]
      }${subscription.billingPeriod !== 1 ? "s" : ""}`
    : "Once off";
};

const getMembershipType = (membership: MembershipBean | Membership) => {
  if (membership.type === "RECURRING") {
    return "recurring" as const;
  }
  if (membership.type === "PREPAID" && !membership.isTrial) {
    return "prepaid" as const;
  }
  return "trial" as const;
};

const getIsDisableEditFieldBecauseOfActiveMembership = (
  isStaff: boolean,
  userSubscriptions: UserMemberSubscriptionBean[],
  fieldId: string,
  // Change 'any' to normal type (after refactoring for EditProfile and UserMemberProfile);
  // Right now we have 'any' in components for values.
  initialValues: any,
) => {
  // If auth from staff portal or zero subscriptions or field (initial) is empty -> not disable
  if (isStaff || userSubscriptions.length === 0 || !initialValues[fieldId]) {
    return false;
  }

  const membershipStatus = userSubscriptions.find((sub) =>
    membershipHelper.isCurrentSubscription(sub),
  )?.status;

  // If membership status is undefined or cancelled or expired -> not disable
  if (
    !membershipStatus ||
    membershipStatus === "CANCELLED" ||
    membershipStatus === "EXPIRED"
  ) {
    return false;
  }

  // For any other cases -> disable field for editing
  return true;
};

const daysFromStartDateToNextBillingDate = (membership: MembershipBean, startDate: string) => {
  const startDateTime = DateTime.fromISO(startDate);
  
  if (membership.billingType === "WEEKLY" && membership.weeklyBillingDay) {
    const billingDayNumber = DateTime.fromFormat(
      capitalize(membership.weeklyBillingDay),
      "cccc",
    ).weekday;
    
    if (startDateTime.weekday === billingDayNumber) return 0;
    
    const nextBillingDate = startDateTime.weekday > billingDayNumber
      ? startDateTime.set({ weekday: billingDayNumber }).plus({ weeks: 1 })
      : startDateTime.set({ weekday: billingDayNumber });
    
    return Math.ceil(nextBillingDate.diff(startDateTime, "days").days);
  }
  
  if (membership.billingType === "MONTHLY" && membership.monthlyBillingDay) {
    const billingDay = membership.monthlyBillingDay;
    
    if (billingDay < 1 || billingDay > 28 || startDateTime.day === billingDay) {
      return 0;
    }
    
    const nextBillingDate = startDateTime.day > billingDay
      ? startDateTime.set({ day: billingDay }).plus({ months: 1 })
      : startDateTime.set({ day: billingDay });
    
    return Math.ceil(nextBillingDate.diff(startDateTime, "days").days);
  }
  
  if (!membership.billingType) return 0;
  
  const timeUnit = billingTypeToTimeUnit[membership.billingType];
  const nextBillingDate = startDateTime.plus({ [timeUnit]: membership.billingPeriod || 1 });
  
  return Math.ceil(nextBillingDate.diff(startDateTime, "days").days);
};


const billingTypeToTimeUnit = {
  MONTHLY: "month",
  WEEKLY: "week",
} satisfies Record<BillingType, DateTimeUnit>;
export const membershipHelper = {
  getBillingDay,
  calculateRecurringDates,
  getSubscriptionBillingRecurrence,
  getBillingRecurrence,
  isCurrentSubscription,
  getMembershipType,
  getIsDisableEditFieldBecauseOfActiveMembership,
  daysFromStartDateToNextBillingDate,
  billingTypeToTimeUnit,
};
